import React, { useState, useMemo } from "react";
import {
    Box,
    Typography,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Collapse,
    Chip,
    IconButton,
    Tooltip,
} from "@mui/material";
import {
    ExpandMore,
    ChevronRight,
    Folder as FolderIcon,
    Description as ProjectIcon,
    Task as TaskIcon,
    Checklist as ChecklistIcon,
    RequestQuote as QuoteIcon,
    Add,
    PlayArrow,
    Edit,
    Delete,
    WarningAmber,
} from "@mui/icons-material";
import styled from "@emotion/styled";

const TreeContainer = styled(Box)(({ theme }) => ({
    borderRight: `1px solid ${theme.palette.divider}`,
    height: "100%",
    overflow: "auto",
}));

const NodeRow = styled(ListItemButton, {
    shouldForwardProp: (prop) => prop !== "$selected" && prop !== "$depth" && prop !== "$retired",
})(({ theme, $selected, $depth, $retired }) => ({
    paddingLeft: theme.spacing(2 + $depth * 2),
    borderLeft: $selected ? `3px solid ${theme.palette.primary.main}` : "3px solid transparent",
    backgroundColor: $selected ? theme.palette.action.selected : "transparent",
    opacity: $retired ? 0.6 : 1,
    "&:hover .node-actions": {
        opacity: 1,
    },
}));

const NodeActions = styled(Box)({
    opacity: 0,
    transition: "opacity 0.15s",
    display: "flex",
    gap: 2,
    "@media (hover: none)": {
        opacity: 1,
    },
});

const ChevronIcon = styled(ListItemIcon)({
    minWidth: 28,
    cursor: "pointer",
});

const NarrowIcon = styled(ListItemIcon)({
    minWidth: 28,
});

const SpacerBox = styled(Box)({
    width: 20,
});

const NodeLabelRow = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
}));

const NodeLabel = styled(Typography, {
    shouldForwardProp: (prop) => prop !== "$missing",
})(({ theme, $missing }) => ({
    flex: 1,
    color: $missing ? theme.palette.warning.main : undefined,
}));

const MissingIcon = styled(WarningAmber)(({ theme }) => ({
    fontSize: theme.typography.pxToRem(16),
    color: theme.palette.warning.main,
}));

const MissingChip = styled(Chip)(({ theme }) => ({
    height: 18,
    fontSize: theme.typography.size.xs,
}));

const CountChip = styled(Chip)(({ theme }) => ({
    height: 18,
    fontSize: theme.typography.size.xs,
}));

const RetiredChip = styled(Chip)(({ theme }) => ({
    height: 18,
    fontSize: theme.typography.size.xs,
}));

const SectionHeader = styled(Box, {
    shouldForwardProp: (prop) => prop !== "$first",
})(({ theme, $first }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing($first ? 1 : 2, 2, 0.5),
    marginTop: $first ? 0 : theme.spacing(0.5),
    borderTop: $first ? "none" : `1px solid ${theme.palette.divider}`,
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
    flex: 1,
    color: theme.palette.text.secondary,
    letterSpacing: "0.08em",
    lineHeight: 1.6,
}));

const EmptyState = styled(Box)(({ theme }) => ({
    padding: theme.spacing(3),
    textAlign: "center",
}));

const TypeIcon = ({ type }) => {
    switch (type) {
        case "group":
            return <FolderIcon fontSize="small" color="primary" />;
        case "project":
            return <ProjectIcon fontSize="small" color="secondary" />;
        case "task":
            return <TaskIcon fontSize="small" color="success" />;
        case "checklist":
            return <ChecklistIcon fontSize="small" color="warning" />;
        case "quote":
            return <QuoteIcon fontSize="small" color="info" />;
        default:
            return null;
    }
};

const getTypeColor = (type) => {
    switch (type) {
        case "group":
            return "primary";
        case "project":
            return "secondary";
        case "task":
            return "success";
        case "quote":
            return "info";
        default:
            return "default";
    }
};

const TYPE_ORDER = { group: 0, project: 1, task: 2, checklist: 3, quote: 4 };

// Work created from a project or task template inherits its skill and department; without a skill
// the work cannot be billed.
const missingFields = (template) => {
    if (template?.templateType !== "project" && template?.templateType !== "task") return [];
    return [!template.skill && "skill", !template.department && "department"].filter(Boolean);
};

const countMissingBelow = (node) =>
    (node.children || []).reduce((count, child) => count + (missingFields(child).length > 0 ? 1 : 0) + countMissingBelow(child), 0);

const SECTIONS = [
    { key: "projects", label: "Projects", types: ["group", "project", "task"] },
    { key: "checklists", label: "Checklists", types: ["checklist"] },
    { key: "quotes", label: "Quotes", types: ["quote"] },
];

const compareTemplates = (a, b) => {
    const byType = (TYPE_ORDER[a.templateType] ?? 99) - (TYPE_ORDER[b.templateType] ?? 99);
    if (byType !== 0) return byType;
    return String(a.displayName || a.name || "").localeCompare(String(b.displayName || b.name || ""), undefined, {
        sensitivity: "base",
        numeric: true,
    });
};

/**
 * Build a tree structure from a flat list of templates.
 * Groups are root nodes; projects nest under their group; tasks nest under their project.
 * Standalone projects (no group), orphan tasks and checklist templates appear at root level.
 * Every level is ordered by template type, then alphabetically by display name.
 */
const buildTree = (templates) => {
    const groups = templates.filter((t) => t.templateType === "group");
    const projects = templates.filter((t) => t.templateType === "project");
    const tasks = templates.filter((t) => t.templateType === "task");
    const checklists = templates.filter((t) => t.templateType === "checklist");
    const quotes = templates.filter((t) => t.templateType === "quote");

    // Index projects and tasks by ID
    const projectMap = {};
    projects.forEach((p) => {
        projectMap[p._id] = { ...p, children: [] };
    });

    const groupMap = {};
    groups.forEach((g) => {
        groupMap[g._id] = { ...g, children: [] };
    });

    // Nest tasks under their project
    const usedTaskIds = new Set();
    tasks.forEach((task) => {
        const projectId = task.project?._id || task.project;
        if (projectId && projectMap[projectId]) {
            projectMap[projectId].children.push({ ...task, children: [] });
            usedTaskIds.add(task._id);
        }
    });

    // Nest projects under their group
    const usedProjectIds = new Set();
    projects.forEach((project) => {
        const groupId = project.group?._id || project.group;
        if (groupId && groupMap[groupId]) {
            groupMap[groupId].children.push(projectMap[project._id]);
            usedProjectIds.add(project._id);
        }
    });

    Object.values(projectMap).forEach((p) => p.children.sort(compareTemplates));
    Object.values(groupMap).forEach((g) => g.children.sort(compareTemplates));

    // Build root nodes
    const rootNodes = [];

    groups.forEach((g) => {
        rootNodes.push(groupMap[g._id]);
    });

    // Standalone projects (no group reference)
    projects.forEach((p) => {
        if (!usedProjectIds.has(p._id)) {
            rootNodes.push(projectMap[p._id]);
        }
    });

    // Orphan tasks (no valid project reference)
    tasks.forEach((t) => {
        if (!usedTaskIds.has(t._id)) {
            rootNodes.push({ ...t, children: [] });
        }
    });

    // Checklist and quote templates never nest - they belong to no group or project
    checklists.forEach((c) => {
        rootNodes.push({ ...c, children: [] });
    });
    quotes.forEach((q) => {
        rootNodes.push({ ...q, children: [] });
    });

    return rootNodes.sort(compareTemplates);
};

const TreeNode = ({
    node,
    depth = 0,
    selectedId,
    onSelect,
    onAddChild,
    onExecute,
    onEdit,
    onDelete,
    canManage,
    canExecute,
    expandedIds,
    onToggleExpand,
}) => {
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expandedIds.has(node._id);
    const isSelected = selectedId === node._id;
    const canAddChild = node.templateType === "group" || node.templateType === "project";
    const canExec = (node.templateType === "group" || node.templateType === "project") && canExecute;
    const childCount = node.children?.length || 0;
    const isRetired = node.isActive === false;
    const missing = missingFields(node);
    const missingBelow = countMissingBelow(node);

    return (
        <>
            <NodeRow
                $selected={isSelected}
                $depth={depth}
                $retired={isRetired}
                onClick={() => onSelect(node)}
            >
                {hasChildren ? (
                    <ChevronIcon
                        onClick={(e) => {
                            e.stopPropagation();
                            onToggleExpand(node._id);
                        }}
                    >
                        {isExpanded ? <ExpandMore fontSize="small" /> : <ChevronRight fontSize="small" />}
                    </ChevronIcon>
                ) : (
                    <NarrowIcon>
                        <SpacerBox />
                    </NarrowIcon>
                )}
                <NarrowIcon>
                    <TypeIcon type={node.templateType} />
                </NarrowIcon>
                <ListItemText
                    primary={
                        <NodeLabelRow>
                            {missing.length > 0 && (
                                <Tooltip title={`No ${missing.join(" or ")} set`}>
                                    <MissingIcon />
                                </Tooltip>
                            )}
                            <NodeLabel variant="body2" noWrap $missing={missing.length > 0}>
                                {node.displayName || node.name}
                            </NodeLabel>
                            {missingBelow > 0 && (
                                <Tooltip title={`${missingBelow} template${missingBelow === 1 ? "" : "s"} inside ${missingBelow === 1 ? "has" : "have"} no skill or department`}>
                                    <MissingChip label={missingBelow} size="small" color="warning" variant="outlined" icon={<MissingIcon />} />
                                </Tooltip>
                            )}
                            {childCount > 0 && (
                                <CountChip label={childCount} size="small" variant="outlined" />
                            )}
                            {isRetired && (
                                <Tooltip title="Retired — kept for reference, never picked for a new quote">
                                    <RetiredChip label="Retired" size="small" variant="outlined" />
                                </Tooltip>
                            )}
                        </NodeLabelRow>
                    }
                />
                <NodeActions className="node-actions">
                    {canAddChild && canManage(node.templateType) && (
                        <Tooltip title={`Add ${node.templateType === "group" ? "Project" : "Task"}`}>
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onAddChild(node);
                                }}
                            >
                                <Add fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                    {canExec && (
                        <Tooltip title="Execute">
                            <IconButton
                                size="small"
                                color="primary"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onExecute(node);
                                }}
                            >
                                <PlayArrow fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                    {canManage(node.templateType) && (
                        <>
                            <Tooltip title="Edit">
                                <IconButton
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onEdit(node);
                                    }}
                                >
                                    <Edit fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                                <IconButton
                                    size="small"
                                    color="error"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onDelete(node);
                                    }}
                                >
                                    <Delete fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </>
                    )}
                </NodeActions>
            </NodeRow>
            {hasChildren && (
                <Collapse in={isExpanded}>
                    <List disablePadding>
                        {node.children.map((child) => (
                            <TreeNode
                                key={child._id}
                                node={child}
                                depth={depth + 1}
                                selectedId={selectedId}
                                onSelect={onSelect}
                                onAddChild={onAddChild}
                                onExecute={onExecute}
                                onEdit={onEdit}
                                onDelete={onDelete}
                                canManage={canManage}
                                canExecute={canExecute}
                                expandedIds={expandedIds}
                                onToggleExpand={onToggleExpand}
                            />
                        ))}
                    </List>
                </Collapse>
            )}
        </>
    );
};

const TemplateTreeView = ({
    templates,
    selectedId,
    onSelect,
    onAddChild,
    onExecute,
    onEdit,
    onDelete,
    canManage,
    canExecute,
}) => {
    const [expandedIds, setExpandedIds] = useState(new Set());

    const tree = useMemo(() => buildTree(templates), [templates]);

    const sections = useMemo(
        () =>
            SECTIONS.map((section) => ({
                ...section,
                nodes: tree.filter((node) => section.types.includes(node.templateType)),
            })).filter((section) => section.nodes.length > 0),
        [tree]
    );

    const handleToggleExpand = (id) => {
        setExpandedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    return (
        <TreeContainer>
            <List disablePadding>
                {sections.map((section, index) => (
                    <React.Fragment key={section.key}>
                        <SectionHeader $first={index === 0}>
                            <SectionTitle variant="overline">{section.label}</SectionTitle>
                            <CountChip label={section.nodes.length} size="small" variant="outlined" />
                        </SectionHeader>
                        {section.nodes.map((node) => (
                            <TreeNode
                                key={node._id}
                                node={node}
                                depth={0}
                                selectedId={selectedId}
                                onSelect={onSelect}
                                onAddChild={onAddChild}
                                onExecute={onExecute}
                                onEdit={onEdit}
                                onDelete={onDelete}
                                canManage={canManage}
                                canExecute={canExecute}
                                expandedIds={expandedIds}
                                onToggleExpand={handleToggleExpand}
                            />
                        ))}
                    </React.Fragment>
                ))}
                {tree.length === 0 && (
                    <EmptyState>
                        <Typography variant="body2" color="text.secondary">
                            No templates found
                        </Typography>
                    </EmptyState>
                )}
            </List>
        </TreeContainer>
    );
};

export { buildTree, missingFields };
export default TemplateTreeView;
